// The installer scope sheet (24 October 2026): the work a crew is asked to assess on one Job, in their language, with
// the crew's own rate per task — never the customer's price. Shared by the command that sends it, the backbone that
// serves the link, the API that drafts it and renders the page, and the CRM panel.
/**
 * CUSTOMER MONEY NEVER REACHES A CREW. A hand-priced Proposal names its sell price inside the line itself —
 * J-1026-10296 on 24 October 2026: "Armstrong Excelon vinyl tile … 159.42 m² at $90.56/m²", "carpet tile, 429.54 m²
 * at $48/m²" — and the work order copied those labels straight to the crew. Every task label, note and day
 * description a crew reads goes through this first. It removes dollar amounts (with an "at", a per-unit suffix and a
 * GST tag) and leaves quantities alone, because "429.54 m²" is the crew's work and "$48/m²" is our revenue.
 */
export function scrubCustomerMoney(text) {
    return String(text ?? '')
        .replace(/\s*(?:\bat\s+|@\s*)?(?:AU)?\$\s?\d[\d,]*(?:\.\d+)?(?:\s*(?:\/|per\s+)\s*(?:m²|m2|sqm|lm|l\/m|each|ea|steps?|pcs?|piece|hr|hour|day|item|unit)(?![A-Za-z]))?(?:\s*(?:\+|inc\.?|incl\.?|ex\.?)\s*GST)?/gi, '')
        .replace(/\s+([,;.)])/g, '$1')
        .replace(/([(])\s+/g, '$1')
        .replace(/\(\s*\)/g, '')
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/(?:\s*[,;·])+\s*$/g, '')
        .trim();
}
/** Every string in a work order a crew reads, scrubbed: the labels come from the Proposal's own lines. */
export function scrubMoneyDeep(value) {
    if (typeof value === 'string')
        return (hasCustomerMoney(value) ? scrubCustomerMoney(value) : value);
    if (Array.isArray(value))
        return value.map((v) => scrubMoneyDeep(v));
    if (value && typeof value === 'object')
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, scrubMoneyDeep(v)]));
    return value;
}
/** True when a dollar figure is still in the text — the guard the tests and the command use. */
export function hasCustomerMoney(text) {
    return /\$\s?\d/.test(String(text ?? ''));
}
const r2 = (n) => Math.round(n * 100) / 100;
/** The fee for an asking price on one task. */
export function askingFee(task, price) {
    if (price.basis === 'total')
        return r2(price.amount_aud);
    return task.quantity === null ? null : r2(price.amount_aud * task.quantity);
}
/** What the crew is paid for the sheet once the answer is in: known rates plus asking prices. Null while a price is missing. */
export function scopeTotal(tasks, prices = []) {
    let known = 0, missing = 0;
    for (const task of tasks) {
        if (task.mode === 'quote') {
            const price = prices.find((p) => p.line_id === task.line_id);
            const fee = price ? askingFee(task, price) : null;
            if (fee === null)
                missing++;
            else
                known += fee;
        }
        else if (task.fee_aud !== null)
            known += task.fee_aud;
    }
    return { known: r2(known), missing, total: missing ? null : r2(known) };
}
/**
 * The site address a crew reads (Jacky, 26 October 2026: "need display full address … only wrote suburb"). Jobs keep the
 * whole address in `street_name` with `street_number` empty ("4/602-606 Princes Highway, Rockdale 2216"), so requiring
 * both fields fell back to the suburb. Unit and number are added when they are filled in; the suburb is added only when
 * the street line does not already say it.
 */
export function jobSiteAddress(j) {
    const clean = (v) => String(v ?? '').trim();
    const unit = clean(j.unit), number = clean(j.street_number), street = clean(j.street_name), suburb = clean(j.suburb);
    if (!street)
        return suburb;
    const lead = number ? `${unit ? `${unit}/` : ''}${number} ` : '';
    const line = `${lead}${street}`;
    return suburb && !line.toLowerCase().includes(suburb.toLowerCase()) ? `${line}, ${suburb}` : line;
}
/** The link's actor identity: one per sheet, so a link can answer only its own sheet. */
export const installerScopeLinkActor = 'installer-scope-link:';
//# sourceMappingURL=installer-scope.js.map